import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDownIcon,
  LogOutIcon,
  SettingsIcon,
  UserIcon,
  GlobeIcon,
  HelpCircleIcon,
  MenuIcon,
  XIcon,
} from 'lucide-react';

const StudentHeader = ({ onMenuClick, menuOpen }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isRTL, currentLanguage, changeLanguage, getCurrentLanguageInfo, getAvailableLanguages } = useLanguage();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const dropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    return user?.username?.charAt(0).toUpperCase() || '?';
  };

  return (
    <header className="w-full bg-gradient-to-b from-white to-sky-50/80 border-b-[3px] border-slate-900/10 shadow-sm">
      <div className="w-full px-3 sm:px-5">
        <div className="flex items-center justify-between gap-3 min-h-[3.5rem] sm:min-h-16 py-2">
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {typeof onMenuClick === 'function' && (
              <button
                type="button"
                onClick={onMenuClick}
                className="flex h-11 w-11 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-2xl border-[3px] border-slate-900/15 bg-white text-slate-900 shadow-[0_4px_0_0_rgba(15,23,42,0.12)] active:translate-y-[2px] active:shadow-[0_2px_0_0_rgba(15,23,42,0.12)] transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
                aria-expanded={typeof menuOpen === 'boolean' ? menuOpen : undefined}
                aria-label={
                  typeof menuOpen === 'boolean' && menuOpen
                    ? t('dashboard.student.closeMenu')
                    : t('dashboard.student.openMenu')
                }
              >
                {typeof menuOpen === 'boolean' && menuOpen ? (
                  <XIcon className="h-6 w-6" aria-hidden />
                ) : (
                  <MenuIcon className="h-6 w-6" aria-hidden />
                )}
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/student')}
              className="flex shrink-0 items-center border-0 bg-transparent p-0 shadow-none outline-none transition-opacity hover:opacity-90 active:opacity-80 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 rounded-sm"
              aria-label={t('navigation.home')}
            >
              <span className="select-none text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                eSoroban
              </span>
            </button>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            <div className="relative" ref={languageDropdownRef}>
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center gap-1.5 p-2 rounded-xl border-2 border-[#0F172A] bg-white hover:bg-[#BEF264] transition-colors"
                title={t('common.language')}
              >
                <GlobeIcon className="w-5 h-5 text-[#0F172A]" />
                <span className="text-sm font-bold">{getCurrentLanguageInfo().flag}</span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isLanguageDropdownOpen && (
                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-44 bg-white rounded-xl border-2 border-[#0F172A] shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] z-50`}>
                  <div className="py-2">
                    {getAvailableLanguages().map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full flex items-center px-4 py-2 text-sm font-medium hover:bg-[#BEF264] transition-colors ${currentLanguage === lang.code ? 'bg-[#BEF264]' : ''}`}
                      >
                        <span className="text-lg mr-2">{lang.flag}</span>
                        <span className="flex-1 text-left">{lang.name}</span>
                        {currentLanguage === lang.code && <span className="text-[#0F172A]">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 p-1.5 rounded-xl border-2 border-[#0F172A] bg-[#0EA5E9] hover:bg-[#38bdf8] transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center border-2 border-[#0F172A]">
                  <span className="text-sm font-bold text-[#0F172A]">{getInitials()}</span>
                </div>
                <span className="hidden sm:inline font-bold text-white text-sm truncate max-w-[100px]">
                  {user?.firstName}
                </span>
                <ChevronDownIcon className={`w-4 h-4 text-white transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-56 bg-white rounded-xl border-2 border-[#0F172A] shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] z-50`}>
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-[#0EA5E9] border-2 border-[#0F172A] flex items-center justify-center">
                        <span className="text-lg font-bold text-white">{getInitials()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#0F172A] truncate">{user?.firstName} {user?.lastName}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <button
                        onClick={() => { navigate('/student/profile'); setIsDropdownOpen(false); }}
                        className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-[#BEF264] rounded-lg transition-colors"
                      >
                        <UserIcon className="w-4 h-4" />
                        <span className={isRTL ? 'mr-3' : 'ml-3'}>{t('navigation.profile')}</span>
                      </button>
                      <button
                        onClick={() => { navigate('/student/settings'); setIsDropdownOpen(false); }}
                        className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-[#BEF264] rounded-lg transition-colors"
                      >
                        <SettingsIcon className="w-4 h-4" />
                        <span className={isRTL ? 'mr-3' : 'ml-3'}>{t('profile.accountSecurity')}</span>
                      </button>
                      <hr className="my-2 border-[#0F172A]" />
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <LogOutIcon className="w-4 h-4" />
                        <span className={isRTL ? 'mr-3' : 'ml-3'}>{t('common.logout')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowHelp(!showHelp)}
        className={`fixed bottom-6 z-40 w-14 h-14 rounded-full bg-[#F472B6] border-4 border-[#0F172A] shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] flex items-center justify-center hover:scale-110 transition-transform ${isRTL ? 'left-6' : 'right-6'}`}
        title={t('dashboard.student.help')}
      >
        <HelpCircleIcon className="w-7 h-7 text-white" />
      </button>
      {showHelp && (
        <div className={`fixed bottom-24 z-40 w-64 p-4 bg-white rounded-2xl border-4 border-[#0F172A] shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] ${isRTL ? 'left-6' : 'right-6'}`}>
          <p className="text-sm font-medium text-[#0F172A]">{t('dashboard.student.helpTip')}</p>
          <button
            type="button"
            onClick={() => setShowHelp(false)}
            className="mt-2 text-xs font-bold text-[#0EA5E9] hover:underline"
          >
            {t('common.close')}
          </button>
        </div>
      )}
    </header>
  );
};

export default StudentHeader;
